import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// PhraseListEditor — a list of short text phrases the operator can add and
// remove (design/components/bot-config-personality.html). Backs both the
// catalog's NeverSayList (frases prohibidas) and HandoffRulesEditor (frases que
// disparan handoff) — same shape: removable chips + an "add" affordance. The
// `tone` only tints the chips; behaviour is identical.

function XIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}
function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

export interface PhraseListEditorProps {
  phrases: string[]
  onAdd: (phrase: string) => void
  onRemove: (index: number) => void
  /** Chip tint: neutral (never-say) or sensitive (handoff triggers). */
  tone?: 'neutral' | 'sens'
  /** Accessible name for the whole group (e.g. "Frases prohibidas"). */
  'aria-label'?: string
  addLabel?: string
  removeLabel?: string
  placeholder?: string
  className?: string
}

/** Removable phrase chips + inline add input, backing both frases prohibidas
 *  and handoff-trigger lists (`tone` only tints the chips). Enter or blur
 *  commits the trimmed draft (empties dropped); Escape cancels. */
export function PhraseListEditor({
  phrases,
  onAdd,
  onRemove,
  tone = 'neutral',
  'aria-label': ariaLabel,
  addLabel,
  removeLabel,
  placeholder,
  className,
}: PhraseListEditorProps) {
  const { t } = useTranslation('components')
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const inputId = useId()

  function commit() {
    const value = draft.trim()
    if (value) onAdd(value)
    setDraft('')
    setAdding(false)
  }

  return (
    <ul aria-label={ariaLabel} className={cn('flex flex-wrap items-center gap-2', className)}>
      {phrases.map((phrase, i) => (
        <li
          key={`${phrase}-${i}`}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-pill py-1 pl-3 pr-1.5 text-[12px] font-medium',
            tone === 'sens' ? 'bg-sens-bg text-sens' : 'bg-field text-text-2',
          )}
        >
          {phrase}
          <button
            type="button"
            onClick={() => onRemove(i)}
            aria-label={`${removeLabel ?? t('phraseList.remove')}: ${phrase}`}
            className="grid size-4 place-items-center rounded-full text-current/70 transition-colors hover:bg-black/10 hover:text-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue [&_svg]:size-3"
          >
            <XIcon />
          </button>
        </li>
      ))}
      <li>
        {adding ? (
          <input
            id={inputId}
            autoFocus
            value={draft}
            placeholder={placeholder ?? t('phraseList.placeholder')}
            aria-label={addLabel ?? t('phraseList.add')}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commit()
              } else if (e.key === 'Escape') {
                setDraft('')
                setAdding(false)
              }
            }}
            className="h-7 w-[180px] rounded-pill border border-input-line bg-surface px-3 text-[12px] text-text outline-none focus-visible:border-brand-blue"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 rounded-pill border border-dashed border-input-line px-2.5 py-1 text-[12px] font-semibold text-text-2 transition-colors hover:border-brand-blue hover:text-link [&_svg]:size-3.5"
          >
            <PlusIcon />
            {addLabel ?? t('phraseList.add')}
          </button>
        )}
      </li>
    </ul>
  )
}
